import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import { buildAcquisitionAnalyticsParams } from "../acquisition/acquisitionSourceFilter";
import { coerceUserAnalyticsNumber } from "./userAnalyticsCoerce";
import {
  USER_ANALYTICS_BY_STATE_ZERO_METRICS,
  type UserAnalyticsByStateHttpBody,
  type UserAnalyticsByStateMetrics,
  type UserAnalyticsByStateQueryParams,
  type UserAnalyticsByStateResult,
} from "./userAnalyticsByStateTypes";

const OTHER_STATE_KEY = "OTHER";

function coerceMetrics(raw: unknown): UserAnalyticsByStateMetrics {
  if (!raw || typeof raw !== "object")
    return { ...USER_ANALYTICS_BY_STATE_ZERO_METRICS };
  const r = raw as Partial<UserAnalyticsByStateMetrics>;
  return {
    new_users: coerceUserAnalyticsNumber(r.new_users),
    ftd_count: coerceUserAnalyticsNumber(r.ftd_count),
    ftd_amount: coerceUserAnalyticsNumber(r.ftd_amount),
    avg_ftd_amount: coerceUserAnalyticsNumber(r.avg_ftd_amount),
    total_users: coerceUserAnalyticsNumber(r.total_users),
  };
}

/**
 * Parses the by-state HTTP envelope into a map keyed by USPS abbreviation.
 *
 * - Strings are uppercased (the backend already emits uppercase, but the map
 *   lookups are case-sensitive).
 * - The `"OTHER"` bucket is dropped — there is no geography to paint for it.
 * - Missing / malformed rows collapse to zero so the chart never crashes.
 */
export function parseUserAnalyticsByStateResponse(
  body: unknown,
): UserAnalyticsByStateResult {
  const b = body as UserAnalyticsByStateHttpBody | null | undefined;
  const rawList = Array.isArray(b?.data) ? b!.data : [];

  const byAbbr: Record<string, UserAnalyticsByStateMetrics> = {};
  for (const row of rawList) {
    const abbr =
      typeof row?.state === "string" ? row.state.trim().toUpperCase() : "";
    if (!abbr || abbr === OTHER_STATE_KEY) continue;
    byAbbr[abbr] = coerceMetrics(row);
  }

  return {
    byAbbr,
    filters: Array.isArray(b?.meta?.filters) ? b!.meta!.filters! : [],
  };
}

/** Fetches the per-state acquisition aggregates for a single date range. */
export async function fetchUserAnalyticsByState(
  params: UserAnalyticsByStateQueryParams,
): Promise<UserAnalyticsByStateResult> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(
    API_ROUTES.GET_USER_ANALYTICS_BY_STATE,
    {
      params: buildAcquisitionAnalyticsParams(params),
    },
  );
  return parseUserAnalyticsByStateResponse(response?.data);
}
